import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, Globe } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLanguage } from '../../contexts/LanguageContext'
import { useCommonTranslation } from '../../hooks/usePageTranslation'
import { translatePath } from '../../config/routes'

const LanguageSelector = ({ className = '', compact = false }) => {
  const {
    currentLanguage,
    getSupportedLanguages,
    isChangingLanguage
  } = useLanguage()

  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useCommonTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  const supportedLanguages = getSupportedLanguages()
  const currentLangInfo = supportedLanguages.find(lang => lang.code === currentLanguage)

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Fechar dropdown quando pressionar Escape
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [])

  const handleLanguageChange = async (newLanguage) => {
    if (newLanguage !== currentLanguage) {
      setIsOpen(false)

      // Navigate to the translated path
      const newPath = translatePath(location.pathname, newLanguage)
      navigate(newPath)

      // i18n will be synced automatically by LocalizedRoutes
      // Also update localStorage for future visits
      localStorage.setItem('i18nextLng', newLanguage)
    }
  }

  const toggleDropdown = () => {
    if (!isChangingLanguage) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Botão do seletor */}
      <button
        onClick={toggleDropdown}
        disabled={isChangingLanguage}
        className={`
          flex items-center space-x-2 px-3 py-2 rounded-lg
          text-secondary-700 hover:text-primary-600 
          hover:bg-secondary-50 transition-colors duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
          ${compact ? 'text-sm' : ''}
          ${isOpen ? 'bg-secondary-50 text-primary-600' : ''}
        `}
        aria-label={t('actions.switchLanguage')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {compact ? (
          <>
            <Globe className="w-4 h-4" />
            <span className="font-medium">{currentLangInfo?.shortCode}</span>
          </>
        ) : (
          <>
            <span className="text-lg" role="img" aria-label="Flag">
              {currentLangInfo?.flag}
            </span>
            <span className="font-medium">{currentLangInfo?.name}</span>
          </>
        )}

        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
            }`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={`
            absolute top-full ${compact ? 'right-0' : 'left-0'} mt-2 
            min-w-[200px] bg-white rounded-lg shadow-lg border border-secondary-200
            py-2 z-50 animate-in fade-in-0 zoom-in-95 duration-200
          `}
          role="menu"
          aria-orientation="vertical"
        >
          {supportedLanguages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className={`
                w-full flex items-center space-x-3 px-4 py-3 text-left
                hover:bg-secondary-50 transition-colors duration-150
                focus:outline-none focus:bg-secondary-50
                ${currentLanguage === language.code ?
                  'bg-primary-50 text-primary-700 font-medium' :
                  'text-secondary-700'
                }
              `}
              role="menuitem"
              disabled={isChangingLanguage}
            >
              <span className="text-lg" role="img" aria-label="Flag">
                {language.flag}
              </span>
              <div className="flex-1">
                <div className="font-medium">{language.name}</div>
                <div className="text-xs text-secondary-500">
                  {language.nativeName}
                </div>
              </div>
              {currentLanguage === language.code && (
                <div className="w-2 h-2 bg-primary-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Loading overlay quando estiver mudando idioma */}
      {isChangingLanguage && (
        <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}

export default LanguageSelector
